import React, { useState, useEffect, useRef } from "react";
import { View, TouchableOpacity, TextInput, Text, StyleSheet, Animated } from "react-native";
import { Button, IconButton } from "react-native-paper";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import WakeWordListener from "./WakeWordListener";
import { useTranslation } from "./i18n/useTranslation";

export default function ChatOverlay() {
  const { t } = useTranslation();
  const [isDarkMode] = useState(false);
  const [activeModule] = useState("pwo");
  const [inputValue, setInputValue] = useState("");
  const [micError, setMicError] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [lastQuestion, setLastQuestion] = useState("");
  const [processing, setProcessing] = useState(false);
  const blinkAnim = useRef(new Animated.Value(1)).current;

  const handleWakeWord = () => { /* your wake-word logic if needed */ };

  // Blink animation for processing state
  useEffect(() => {
    if (processing) {
      Animated.loop(
        Animated.sequence([
          Animated.timing(blinkAnim, { toValue: 0.3, duration: 500, useNativeDriver: true }),
          Animated.timing(blinkAnim, { toValue: 1, duration: 500, useNativeDriver: true }),
        ])
      ).start();
    } else {
      blinkAnim.setValue(1);
    }
  }, [processing]);
  
  // Handle external "processing done" signal
  useEffect(() => {
    window.onProcessingDone = () => {
      setProcessing(false);
    };
    return () => { window.onProcessingDone = null; };
  }, []);

  // Clear processing when answer arrives
  useEffect(() => {
    window.onChatOverlayAnswer = () => {
      setProcessing(false);
    };
    return () => { window.onChatOverlayAnswer = null; };
  }, []);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.type === "SCREENSHOT_TAKEN") {
        const { imagebytes, scenename } = event.data;
        console.log("📤 ChatOverlay → forwarding to AndroidBridge:", {
          imagebytes: imagebytes.slice(0,50) + "...",
          scenename
        });
        window.AndroidBridge.ScreenshotFromUnity(imagebytes);
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);
  
  useEffect(() => {
    window.onChatOverlayTakeScreenshot = (configB64) => {
      console.log("📸 ChatOverlay: takeScreenshot(", configB64, ")");
      window.AndroidBridge.takeScreenshot(configB64);
    };
    return () => { window.onChatOverlayTakeScreenshot = null; };
  }, []);
  
  const handleVoiceInput = () => {
    if (!recording) {
      console.log("🎤 Starting recording...");
      window.AndroidBridge?.startRecording?.();
      setRecording(true);
      setSecondsLeft(7);

      const interval = setInterval(() => {
        setSecondsLeft((s) => {
          if (s <= 1) {
            clearInterval(interval);
            window.AndroidBridge?.stopRecording?.();
            setRecording(false);
            return 0;
          }
          return s - 1;
        });
      }, 1000);
  
      setTimeout(() => {
        console.log("⏱️ 7.5s up — stopping recording");
        window.AndroidBridge?.stopRecording?.();
        setRecording(false);
      }, 7500);

    } else {
      // ignore taps while recording
    }
  };
  
  const handleAskButtonPress = () => {
    if (inputValue.trim()) {
      // display and blink the question
      setLastQuestion(inputValue);
      setProcessing(true);
      console.log("✉️ Sending typed question:", inputValue);
      window.AndroidBridge?.onAskText?.(inputValue);
      setInputValue("");
    } else {
      console.warn("⚠️ No input or voice recorded!");
    }
  };

  const stopAudio = () => { window.AndroidBridge?.onStopAudio?.(); };

  // Android sends voice file here → trigger voice ask
  useEffect(() => {
    window.onVoiceFile = (filename, base64data) => {
      // show placeholder for voice question
      setLastQuestion("Voice question…");
      setProcessing(true);
      console.log("🎙️ Voice file received:", filename);
      window.AndroidBridge?.onAskVoice?.(filename, base64data);
    };
    return () => { window.onVoiceFile = null; };
  }, []);

  return (
    <View style={styles.bottomBar(isDarkMode)}>
      <WakeWordListener onWakeWordDetected={handleWakeWord} />

      <View style={{ alignItems: "center", marginHorizontal: 8 }}>
        <TouchableOpacity onPress={handleVoiceInput}>
          <MaterialCommunityIcons
            name="microphone"
            size={30}
            color={recording ? "#E53935" : (isDarkMode ? "#E0E6F0" : "#333")}
          />
        </TouchableOpacity>
        {recording && (
          <Text style={{ color: "#E53935", fontSize: 12 }}>
            ● {secondsLeft}s
          </Text>
        )}
        {micError && <Text style={styles.micError}>{micError}</Text>}
      </View>

      {processing ? (
        <Animated.View style={{ flex: 1, justifyContent: "center" }}>
          <Animated.Text
            style={[
              { opacity: blinkAnim, fontSize: 16 },
              { color: isDarkMode ? "#E0E6F0" : "#333" },
            ]}
          >
            {lastQuestion}
          </Animated.Text>
        </Animated.View>
      ) : (
        <TextInput
          style={styles.inputBox(isDarkMode)}
          placeholder={
            activeModule === "video"
              ? t("Ask a question about this video…")
              : activeModule === "pwo"
              ? t("Ask a question about this Interactive Play With Object…")
              : t("Ask a question…")
          }
          value={inputValue}
          onChangeText={setInputValue}
          onSubmitEditing={handleAskButtonPress}
          returnKeyType="send"
        />
      )}

      <Button onPress={handleAskButtonPress} mode="contained">
        {t("Ask")}
      </Button>

      <IconButton
        icon={() => (
          <MaterialCommunityIcons
            name={isMuted ? "volume-off" : "volume-high"}
            size={24}
            color={isDarkMode ? "#E0E6F0" : "#333"}
          />
        )}
        onPress={() => {
          setIsMuted(!isMuted);
          if (!isMuted) stopAudio();
        }}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  bottomBar: (isDark) => ({
    flexDirection: "row",
    alignItems: "center",
    padding: 10,
    backgroundColor: isDark ? "#1e1e1e" : "#fff",
    width: "100%",
    height: 60,
  }),
  inputBox: (isDark) => ({
    flex: 1,
    marginHorizontal: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: isDark ? "#333" : "#f0f0f0",
    borderRadius: 4,
  }),
  micError: {
    color: "red",
    fontSize: 10,
    marginTop: 4,
    maxWidth: 100,
    textAlign: "center",
  },
});

// // frontend/app/ChatOverlay.js
// import React, { useState, useEffect } from "react";
// import { View, TouchableOpacity, TextInput, Text, StyleSheet } from "react-native";
// import { Button, IconButton } from "react-native-paper";
// import { MaterialCommunityIcons } from "@expo/vector-icons";
// import WakeWordListener from "./WakeWordListener";
// import { useTranslation } from "./i18n/useTranslation";

// export default function ChatOverlay() {
//   const { t } = useTranslation();
//   const [isDarkMode] = useState(false);
//   const [activeModule] = useState("pwo");
//   const [inputValue, setInputValue] = useState("");
//   const [micError, setMicError] = useState(null);
//   const [isMuted, setIsMuted] = useState(false);
//   const [recording, setRecording] = useState(false);
//   const [audioBlob, setAudioBlob] = useState(null);
//   const [secondsLeft, setSecondsLeft] = useState(0);

//   const handleWakeWord = () => { /* your wake-word logic if needed */ };

//   useEffect(() => {
//     const onMessage = (event) => {
//       if (event.data?.type === "SCREENSHOT_TAKEN") {
//         const { imagebytes, scenename } = event.data;
//         console.log("📤 ChatOverlay → forwarding to AndroidBridge:", {
//           imagebytes: imagebytes.slice(0,50) + "...",
//           scenename
//         });
//         // call your @JavascriptInterface
//         window.AndroidBridge.ScreenshotFromUnity(imagebytes);
//       }
//     };
  
//     window.addEventListener("message", onMessage);
//     return () => window.removeEventListener("message", onMessage);
//   }, []);
  
//   useEffect(() => {
//     window.onChatOverlayTakeScreenshot = (configB64) => {
//       console.log("📸 ChatOverlay: takeScreenshot(", configB64, ")");
//       window.AndroidBridge.takeScreenshot(configB64);
//     };
//     return () => {
//       window.onChatOverlayTakeScreenshot = null;
//     };
//   }, []);
  
//   const handleVoiceInput = () => {
//     if (!recording) {
//       console.log("🎤 Starting recording...");
//       window.AndroidBridge?.startRecording?.();
//       setRecording(true);
//       setSecondsLeft(7);

//       const interval = setInterval(() => {
//         setSecondsLeft((s) => {
//           if (s <= 1) {
//             clearInterval(interval);
//             window.AndroidBridge?.stopRecording?.();
//             setRecording(false);
//             return 0;
//           }
//           return s - 1;
//         });
//       }, 1000);
  
//       // stop recording after 7 seconds
//       setTimeout(() => {
//         console.log("⏱️ 7.5s up — stopping recording");
//         window.AndroidBridge?.stopRecording?.();
//         setRecording(false);
//       }, 7500);
  
//     } else {
//       // (optional) ignore extra taps while recording
//     }
//   };
  
//   const handleAskButtonPress = () => {
//     if (inputValue.trim()) {
//       console.log("✉️ Sending typed question:", inputValue);
//       // window.parent.postMessage({ type: "ASK_TEXT", text: inputValue }, "*");
//       window.AndroidBridge?.onAskText?.(inputValue);

//       setInputValue("");
//     } 
//     else {
//       console.warn("⚠️ No input or voice recorded!");
//     }
//   };

//   const stopAudio = () => { window.AndroidBridge?.onStopAudio?.(); };

//   // 🔵 Android will send the voice file here
//   useEffect(() => {
//     window.onVoiceFile = (filename, base64data) => {
//       console.log("🎙️ Voice file received:", filename);
//       // setAudioBlob({ filename, base64: base64data });
//       window.AndroidBridge?.onAskVoice?.(filename, base64data);
//       // setInputValue(""); // Clear text input
//     };
//     return () => {
//       window.onVoiceFile = null;
//     };
//   }, []);

//   return (
//     <View style={styles.bottomBar(isDarkMode)}>
//       <WakeWordListener onWakeWordDetected={handleWakeWord} />

//       <View style={{ alignItems: "center", marginHorizontal: 8 }}>
//         <TouchableOpacity onPress={handleVoiceInput}>
//           <MaterialCommunityIcons
//             // name={recording ? "microphone-off" : "microphone"}
//             name={"microphone"}
//             size={30}
//             color={isDarkMode ? "#E0E6F0" : "#333"}
//           />
//         </TouchableOpacity>
//         {recording && (
//           <Text style={{ color: recording ? "#E53935" : "#333", fontSize: 12 }}>
//             ● {secondsLeft}s
//           </Text>
//         )}

//         {micError && <Text style={styles.micError}>{micError}</Text>}
//       </View>

//       <TextInput
//         style={styles.inputBox(isDarkMode)}
//         placeholder={
//           activeModule === "video"
//             ? t("Ask a question about this video…")
//             : activeModule === "pwo"
//             ? t("Ask a question about this Interactive Play With Object…")
//             : t("Ask a question…")
//         }
//         value={inputValue}
//         onChangeText={setInputValue}
//         onSubmitEditing={handleAskButtonPress}
//         returnKeyType="send"
//       />

//       <Button onPress={handleAskButtonPress} mode="contained">
//         {t("Ask")}
//       </Button>

//       <IconButton
//         icon={() => (
//           <MaterialCommunityIcons
//             name={isMuted ? "volume-off" : "volume-high"}
//             size={24}
//             color={isDarkMode ? "#E0E6F0" : "#333"}
//           />
//         )}
//         onPress={() => {
//           setIsMuted(!isMuted);
//           if (!isMuted) stopAudio();
//         }}
//       />
//     </View>
//   );
// }

// const styles = StyleSheet.create({
//   bottomBar: (isDark) => ({
//     flexDirection: "row",
//     alignItems: "center",
//     padding: 10,
//     backgroundColor: isDark ? "#1e1e1e" : "#fff",
//     width: "100%",
//     height: 60,
//   }),
//   inputBox: (isDark) => ({
//     flex: 1,
//     marginHorizontal: 8,
//     paddingHorizontal: 10,
//     paddingVertical: 6,
//     backgroundColor: isDark ? "#333" : "#f0f0f0",
//     borderRadius: 4,
//   }),
//   micError: {
//     color: "red",
//     fontSize: 10,
//     marginTop: 4,
//     maxWidth: 100,
//     textAlign: "center",
//   },
// });

